import React, { useState } from 'react';
import { SkillNode } from '../../data/skillTaxonomy';
import { SkillLevel } from '../../services/businessIdeaTypes';

interface SkillRecommendationsProps {
  recommendations: Array<{
    skill: string;
    description?: string;
    source: string;
    relationship: string;
  }>;
  onAddSkill: (skill: { id: string; name: string; level: SkillLevel }) => void;
}

const SkillRecommendations: React.FC<SkillRecommendationsProps> = ({ recommendations, onAddSkill }) => {
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);

  const handleAddSkill = (rec: typeof recommendations[0], level: SkillLevel) => {
    const skillId = rec.skill.toLowerCase().replace(/\s+/g, '_');
    onAddSkill({
      id: skillId,
      name: rec.skill,
      level
    });
    setExpandedSkill(null);
  };

  const getLevelButtonClass = (isSelected: boolean) => `
    px-3 py-1 text-sm rounded-full transition-colors
    ${isSelected 
      ? 'bg-blue-600 text-white' 
      : 'bg-white text-blue-600 hover:bg-blue-50'
    }
  `;

  // Define skill levels array to match main taxonomy
  const skillLevels: SkillLevel[] = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <div className="grid gap-3">
        {recommendations.map((rec) => (
          <div 
            key={rec.skill}
            className={`
              bg-white rounded-lg p-4 transition-all duration-200 shadow-sm
              ${expandedSkill === rec.skill ? 'ring-2 ring-blue-400' : 'hover:shadow-md'}
            `}
          >
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-gray-800">{rec.skill}</h4>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                    <span className="inline-flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                        <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                      </svg>
                      {rec.source}
                    </span>
                    <span className="inline-flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd"/>
                      </svg>
                      {rec.relationship}
                    </span>
                  </div>
                </div>
                <button 
                  onClick={() => setExpandedSkill(expandedSkill === rec.skill ? null : rec.skill)}
                  className="text-blue-600 hover:text-blue-800 p-2 rounded-full hover:bg-blue-50"
                >
                  <svg 
                    className={`w-5 h-5 transform transition-transform ${expandedSkill === rec.skill ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {rec.description && (
                <p className="text-sm text-gray-600">{rec.description}</p>
              )}

              {expandedSkill === rec.skill && (
                <div className="mt-3">
                  <p className="text-sm font-medium text-gray-700 mb-2">Select your experience level:</p>
                  <div className="flex flex-wrap gap-2">
                    {skillLevels.map((level) => (
                      <button
                        key={level}
                        onClick={() => handleAddSkill(rec, level)}
                        className={getLevelButtonClass(false)}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SkillRecommendations;
